import type { IChipEvent } from '../models';

// Segmento con timestamp restituito da Whisper (response_format=verbose_json), secondi
// relativi all'inizio di QUESTA registrazione (non del colloquio intero).
export interface ITranscriptSegment {
  start: number;
  end: number;
  text: string;
}

const UNDO_WINDOW_MS = 10_000;

export const pushChipEvent = (
  events: IChipEvent[],
  buttonId: string,
  color: string,
  topicId: string,
  note?: string,
): IChipEvent[] => [
  ...events,
  { t: new Date().toISOString(), topicId, buttonId, color, note, undone: false },
];

// "Annulla ultimo": marca undone=true entro 10s dal tap, senza mai rimuovere l'evento dal log.
export const undoLastChipEvent = (events: IChipEvent[]): IChipEvent[] => {
  let lastIdx = -1;
  for (let i = events.length - 1; i >= 0; i--) {
    if (!events[i].undone) {
      lastIdx = i;
      break;
    }
  }
  if (lastIdx === -1) {
    return events;
  }
  if (Date.now() - new Date(events[lastIdx].t).getTime() > UNDO_WINDOW_MS) {
    return events;
  }
  return events.map((e, i) => (i === lastIdx ? { ...e, undone: true } : e));
};

// Conteggio dei tap per pulsante (per il "V1 ×3" mostrato all'architetto). N1 è testo libero,
// non si conta.
export const chipCounts = (events: IChipEvent[]): Record<string, number> => {
  const out: Record<string, number> = {};
  events
    .filter((e) => !e.undone && e.buttonId !== 'N1')
    .forEach((e) => (out[e.buttonId] = (out[e.buttonId] ?? 0) + 1));
  return out;
};

// Colloca ogni tap nel punto della trascrizione in cui è avvenuto, correlando il suo timestamp
// all'offset (in secondi, da recStartAt) del segmento Whisper più vicino. Se non ci sono
// segmenti con timing (fallback senza verbose_json), il testo esce senza tag inline.
export const insertInlineMarkers = (
  segments: ITranscriptSegment[],
  events: IChipEvent[],
  recStartAt: number,
): string => {
  const joined = segments.map((s) => s.text.trim()).join(' ').trim();
  const active = events.filter((e) => !e.undone);
  if (!segments.length || !active.length) {
    return joined;
  }
  const elapsedOf = (e: IChipEvent): number => (new Date(e.t).getTime() - recStartAt) / 1000;
  const segIndexFor = (elapsed: number): number => {
    const hit = segments.findIndex((s) => elapsed >= s.start && elapsed < s.end);
    if (hit >= 0) {
      return hit;
    }
    return elapsed < segments[0].start ? 0 : segments.length - 1;
  };
  const bySegment = new Map<number, IChipEvent[]>();
  active.forEach((e) => {
    const idx = segIndexFor(elapsedOf(e));
    bySegment.set(idx, [...(bySegment.get(idx) ?? []), e]);
  });
  return segments
    .map((seg, i) => {
      const tag = tagFor(bySegment.get(i) ?? []);
      return tag ? `${seg.text.trim()} ${tag}` : seg.text.trim();
    })
    .join(' ')
    .trim();
};

const tagFor = (events: IChipEvent[]): string => {
  if (!events.length) {
    return '';
  }
  const counts: Record<string, number> = {};
  events
    .filter((e) => e.buttonId !== 'N1')
    .forEach((e) => (counts[e.buttonId] = (counts[e.buttonId] ?? 0) + 1));
  const tags = Object.entries(counts).map(([id, n]) => (n > 1 ? `[${id} ×${n}]` : `[${id}]`));
  events
    .filter((e) => e.buttonId === 'N1')
    .forEach((e) => tags.push(`[NOTA: "${(e.note ?? '').trim()}"]`));
  return tags.join('');
};
